// ===========================================================================
// Whitebox Inspector Component
// Tabbed interface for inspecting pipeline internals
// ===========================================================================

import React, { useRef, useEffect } from 'react';
import {
  FrameData,
  InspectorTab,
  UIState,
  GRID_COLS,
  GRID_ROWS,
} from '../types';
import { renderGridToCanvas } from '../utils/rendering';
import './Inspector.css';

interface InspectorProps {
  frameData: FrameData | null;
  uiState: UIState;
  onTabChange: (tab: InspectorTab) => void;
  onSelectTrack: (trackId: number | null) => void;
}

export const Inspector: React.FC<InspectorProps> = ({
  frameData,
  uiState,
  onTabChange,
  onSelectTrack,
}) => {
  const tabs: { id: InspectorTab; label: string; icon: string }[] = [
    { id: 'detections', label: 'Detections', icon: '📦' },
    { id: 'tracks', label: 'Tracks', icon: '🔗' },
    { id: 'depth', label: 'Depth', icon: '🏔' },
    { id: 'heatmap', label: 'Heatmap', icon: '🔥' },
    { id: 'contours', label: 'Contours', icon: '⭕' },
    { id: 'reasoning', label: 'Reasoning', icon: '💡' },
  ];

  return (
    <div className="inspector">
      <div className="inspector-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`inspector-tab ${uiState.inspectorTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="inspector-content">
        {uiState.inspectorTab === 'detections' && (
          <DetectionsPanel frameData={frameData} />
        )}
        {uiState.inspectorTab === 'tracks' && (
          <TracksPanel 
            frameData={frameData} 
            selectedTrackId={uiState.selectedTrackId}
            onSelectTrack={onSelectTrack}
          />
        )}
        {uiState.inspectorTab === 'depth' && (
          <DepthPanel frameData={frameData} />
        )}
        {uiState.inspectorTab === 'heatmap' && (
          <HeatmapPanel frameData={frameData} />
        )}
        {uiState.inspectorTab === 'contours' && (
          <ContoursPanel frameData={frameData} />
        )}
        {uiState.inspectorTab === 'reasoning' && (
          <ReasoningPanel frameData={frameData} />
        )}
      </div>
    </div>
  );
};

// Detections Panel
const DetectionsPanel: React.FC<{ frameData: FrameData | null }> = ({ frameData }) => {
  if (!frameData) return <EmptyState message="No frame data available" />;

  const personDets = frameData.detections.filter(d => d.cls === 'person');
  const weaponDets = frameData.detections.filter(d => d.cls === 'weapon');

  return (
    <div className="panel-content">
      <div className="stats-row">
        <div className="stat">
          <span className="stat-value">{personDets.length}</span>
          <span className="stat-label">People</span>
        </div>
        <div className="stat">
          <span className="stat-value stat-weapon">{weaponDets.length}</span>
          <span className="stat-label">Weapons</span>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Class</th>
              <th>Conf</th>
              <th>Center</th>
              <th>Size</th>
            </tr>
          </thead>
          <tbody>
            {frameData.detections.map((det, i) => (
              <tr key={i} className={det.cls === 'weapon' ? 'row-weapon' : ''}>
                <td>
                  <span className={`badge badge-${det.cls}`}>
                    {det.cls}
                  </span>
                </td>
                <td className="mono">{(det.conf * 100).toFixed(1)}%</td>
                <td className="mono">
                  ({det.cx.toFixed(0)}, {det.cy.toFixed(0)})
                </td>
                <td className="mono">
                  {(det.x2 - det.x1).toFixed(0)}×{(det.y2 - det.y1).toFixed(0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Tracks Panel
const TracksPanel: React.FC<{
  frameData: FrameData | null;
  selectedTrackId: number | null;
  onSelectTrack: (trackId: number | null) => void;
}> = ({ frameData, selectedTrackId, onSelectTrack }) => {
  if (!frameData) return <EmptyState message="No frame data available" />;

  const confirmedTracks = frameData.tracks.filter(t => t.confirmed);
  const pendingTracks = frameData.tracks.filter(t => !t.confirmed);

  return (
    <div className="panel-content">
      <div className="stats-row">
        <div className="stat">
          <span className="stat-value">{confirmedTracks.length}</span>
          <span className="stat-label">Confirmed</span>
        </div>
        <div className="stat">
          <span className="stat-value stat-pending">{pendingTracks.length}</span>
          <span className="stat-label">Pending</span>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Label</th>
              <th>Hits</th>
              <th>Age</th>
              <th>Position</th>
            </tr>
          </thead>
          <tbody>
            {frameData.tracks.map((track) => (
              <tr 
                key={track.trackId}
                className={`
                  ${track.label === 'suspect' ? 'row-suspect' : ''}
                  ${selectedTrackId === track.trackId ? 'row-selected' : ''}
                  ${!track.confirmed ? 'row-pending' : ''}
                `}
                onClick={() => onSelectTrack(
                  selectedTrackId === track.trackId ? null : track.trackId
                )}
              >
                <td className="mono">#{track.trackId}</td>
                <td>
                  <span className={`badge badge-${track.label}`}>
                    {track.label}
                  </span>
                </td>
                <td className="mono">{track.hits}</td>
                <td className="mono">{track.age}</td>
                <td className="mono">
                  ({track.cx.toFixed(0)}, {track.cy.toFixed(0)})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Depth Panel
const DepthPanel: React.FC<{ frameData: FrameData | null }> = ({ frameData }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!frameData || !canvasRef.current) return;
    renderGridToCanvas(frameData.depthGrid, canvasRef.current, 'viridis', true);
  }, [frameData]);

  if (!frameData) return <EmptyState message="No frame data available" />;

  return (
    <div className="panel-content">
      <div className="grid-preview">
        <canvas 
          ref={canvasRef} 
          className="preview-canvas"
          style={{ width: '100%', height: 'auto', imageRendering: 'pixelated' }}
        />
      </div>
      <div className="grid-info">
        <p>Grid size: {GRID_COLS}×{GRID_ROWS}</p>
        <p>Mode: Perspective Y-depth (closer = higher Y)</p>
        <p className="text-muted">
          Depth values range from 0.0 (far/top) to 1.0 (close/bottom)
        </p>
      </div>
    </div>
  );
};

// Heatmap Panel
const HeatmapPanel: React.FC<{ frameData: FrameData | null }> = ({ frameData }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!frameData || !canvasRef.current) return;
    renderGridToCanvas(frameData.heatmapGrid, canvasRef.current, 'heat', true);
  }, [frameData]);

  if (!frameData) return <EmptyState message="No frame data available" />;

  // Find max heatmap value
  let maxVal = 0;
  for (const row of frameData.heatmapGrid) {
    for (const val of row) {
      maxVal = Math.max(maxVal, val);
    }
  }

  return (
    <div className="panel-content">
      <div className="grid-preview">
        <canvas 
          ref={canvasRef} 
          className="preview-canvas"
          style={{ width: '100%', height: 'auto', imageRendering: 'pixelated' }}
        />
      </div>
      <div className="grid-info">
        <p>Gatherings: {frameData.gatherings.length}</p>
        <p>Max intensity: {maxVal.toFixed(3)}</p>
        <p>Threshold: {frameData.metrics.thresholds.heatmapThreshold}</p>
      </div>
    </div>
  );
};

// Contours Panel
const ContoursPanel: React.FC<{ frameData: FrameData | null }> = ({ frameData }) => {
  if (!frameData) return <EmptyState message="No frame data available" />;

  return (
    <div className="panel-content">
      <div className="stats-row">
        <div className="stat">
          <span className="stat-value">{frameData.crowdRegions.length}</span>
          <span className="stat-label">Regions</span>
        </div>
        <div className="stat">
          <span className={`stat-value ${frameData.flags.crowd ? 'stat-crowd' : ''}`}>
            {frameData.flags.crowd ? 'YES' : 'NO'}
          </span>
          <span className="stat-label">Crowd</span>
        </div>
        <div className="stat">
          <span className={`stat-value ${frameData.flags.mob ? 'stat-mob' : ''}`}>
            {frameData.flags.mob ? 'YES' : 'NO'}
          </span>
          <span className="stat-label">Mob</span>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Region</th>
              <th>Inside</th>
              <th>Area</th>
              <th>Weapon</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {frameData.crowdRegions.map((region) => (
              <tr 
                key={region.id}
                className={region.mob ? 'row-mob' : region.insideCount >= frameData.metrics.thresholds.crowdThreshold ? 'row-crowd' : ''}
              >
                <td className="mono">{region.id}</td>
                <td className="mono">{region.insideCount}</td>
                <td className="mono">{region.area.toFixed(0)}</td>
                <td>
                  {region.weaponInRegion ? (
                    <span className="badge badge-weapon">YES</span>
                  ) : (
                    <span className="text-muted">no</span>
                  )}
                </td>
                <td>
                  {region.mob ? (
                    <span className="badge badge-mob">MOB</span>
                  ) : region.insideCount >= frameData.metrics.thresholds.crowdThreshold ? (
                    <span className="badge badge-crowd">CROWD</span>
                  ) : (
                    <span className="text-muted">normal</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Reasoning Panel
const ReasoningPanel: React.FC<{ frameData: FrameData | null }> = ({ frameData }) => {
  if (!frameData) return <EmptyState message="No frame data available" />;

  return (
    <div className="panel-content">
      <div className="reasoning-list">
        {frameData.reasoning.map((line, i) => (
          <div 
            key={i} 
            className={`reasoning-item ${
              line.includes('MOB') ? 'reasoning-mob' :
              line.includes('CROWD') ? 'reasoning-crowd' :
              line.includes('⚠') ? 'reasoning-warning' :
              line.includes('✅') ? 'reasoning-ok' : ''
            }`}
          >
            {line}
          </div>
        ))}
      </div>

      <div className="thresholds-summary">
        <h4>Active Thresholds</h4>
        <div className="threshold-grid">
          <span>Crowd:</span>
          <span className="mono">{frameData.metrics.thresholds.crowdThreshold}</span>
          <span>Min Group:</span>
          <span className="mono">{frameData.metrics.thresholds.minGroupSize}</span>
          <span>Spatial Dist:</span>
          <span className="mono">{frameData.metrics.thresholds.spatialDistanceThreshold}px</span>
          <span>Depth Δ:</span>
          <span className="mono">{frameData.metrics.thresholds.depthThreshold}</span>
        </div>
      </div>
    </div>
  );
};

// Empty State
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="empty-state">
    <span className="empty-icon">📭</span>
    <p>{message}</p>
  </div>
);
